import { createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'src/store';
import { ProviderClient, SelectedClientList } from 'src/types/client';
import api from 'src/requests/axiosConfig';

interface ClientsState {
  isModalOpen: boolean;
  currentClient: any;
  totalClients: number;
  clientListFetched: boolean;
  clientList: ProviderClient[];
  allClients: ProviderClient[];
  allActiveClients: ProviderClient[];
  selectedClients: any;
}

const initialState: ClientsState = {
  isModalOpen: false,
  currentClient: undefined,
  totalClients: 0,
  clientList: [],
  allClients: [],
  allActiveClients: [],
  clientListFetched: false,
  selectedClients: [],
};

const slice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    getClientList(
      state: ClientsState,
      action: PayloadAction<{ clients: ProviderClient[]; totalClients: number }>
    ) {
      const { clients, totalClients } = action.payload;
      state.totalClients = totalClients;
      state.clientList = clients;
      state.clientListFetched = true;
    },
    getAllClients(state: ClientsState, action: PayloadAction<{ allClients: ProviderClient[] }>) {
      const { allClients } = action.payload;
      state.allClients = allClients;
    },
    getAllActiveClients(
      state: ClientsState,
      action: PayloadAction<{ allActiveClients: ProviderClient[] }>
    ) {
      const { allActiveClients } = action.payload;
      state.allActiveClients = allActiveClients;
    },
    createClient(state: ClientsState, action: PayloadAction<boolean>) {
      state.isModalOpen = false;
    },
    getCurrentClient(state: ClientsState, action: PayloadAction<any>) {
      state.currentClient = action.payload;
    },
    setSelectedClients(
      state: ClientsState,
      action: PayloadAction<{ clients: SelectedClientList[] }>
    ) {
      const { clients } = action.payload;
      state.selectedClients = clients;
    },
    openModal(state: ClientsState) {
      state.isModalOpen = true;
    },
    closeModal(state: ClientsState) {
      state.isModalOpen = false;
    },
  },
});

export const reducer = slice.reducer;

export const openModal = (): AppThunk => (dispatch) => {
  dispatch(slice.actions.openModal());
};

export const closeModal = (): AppThunk => (dispatch) => {
  dispatch(slice.actions.closeModal());
};

export const setSelectedClients =
  (data: any): AppThunk =>
  (dispatch) => {
    dispatch(slice.actions.setSelectedClients({ clients: data }));
  };

export const createClient =
  (data: any): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.createClient(undefined));
  };

export const getAllClients = (): AppThunk => async (dispatch) => {
  let allClientsFetched = false;
  const allClientsList = [];
  let page = 0;
  try {
    const therapistId = localStorage.getItem('userId');
    while (!allClientsFetched) {
      const clientResponse = await api({
        method: 'get',
        url: `v2/therapist/${therapistId}/clients`,
        params: {
          startFrom: page * 10,
          limit: 10,
        },
      });

      page += 1;
      allClientsList.push(...clientResponse.data.clients);

      if (clientResponse.data.totalClients === allClientsList.length) {
        allClientsFetched = true;
      }
    }

    dispatch(slice.actions.getAllClients({ allClients: allClientsList }));
  } catch (error) {
    console.error('Redux ERROR:', error);
    dispatch(slice.actions.getAllClients({ allClients: [] }));
  }
};

export const getAllActiveClients = (): AppThunk => async (dispatch) => {
  let allClientsFetched = false;
  const allActiveClientsList = [];
  let page = 0;
  try {
    const therapistId = localStorage.getItem('userId');
    while (!allClientsFetched) {
      const clientResponse = await api({
        method: 'get',
        url: `v2/therapist/${therapistId}/clients`,
        params: {
          startFrom: page * 10,
          limit: 10,
          userStatus: 'active',
        },
      });

      page += 1;
      allActiveClientsList.push(...clientResponse.data.clients);

      if (clientResponse.data.totalClients === allActiveClientsList.length) {
        allClientsFetched = true;
      }
    }

    dispatch(slice.actions.getAllActiveClients({ allActiveClients: allActiveClientsList }));
  } catch (error) {
    console.error('Redux ERROR (active clients):', error);
    dispatch(slice.actions.getAllActiveClients({ allActiveClients: [] }));
  }
};

export const getClientList =
  ({
    startFrom,
    searchTerm,
    limit,
    sortKey,
    sortOrder,
    userStatus,
    operatorId,
    isOwned,
    signal,
  }: {
    startFrom?: number;
    searchTerm?: string;
    limit?: number;
    sortKey?: string;
    sortOrder?: string;
    userStatus?: string;
    operatorId?: string;
    isOwned?: boolean;
    signal?: AbortSignal;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const therapistId = localStorage.getItem('userId');
      const apiParams: any = {
        startFrom: (startFrom ? startFrom : 0) * (limit ? limit : 10),
        limit: limit || 10,
      };

      if (searchTerm) {
        apiParams['searchTerm'] = searchTerm;
      }
      if (sortKey) {
        apiParams['sortKey'] = sortKey;
      }
      if (sortOrder) {
        apiParams['sortOrder'] = sortOrder;
      }
      if (userStatus) {
        apiParams['userStatus'] = userStatus;
      }
      if (operatorId) {
        apiParams['operatorId'] = operatorId;
      }
      if (isOwned) {
        apiParams['isOwned'] = isOwned;
      }

      const response = await api({
        method: 'get',
        url: `v2/therapist/${therapistId}/clients`,
        params: apiParams,
        signal,
      });

      dispatch(
        slice.actions.getClientList({
          clients: response.data.clients || [],
          totalClients: response.data.totalClients || 0,
        })
      );
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Failed to fetch client list:', error);
      dispatch(slice.actions.getClientList({ clients: [], totalClients: 0 }));
    }
  };

export const getCurrentClient =
  (therapistId: string, clientId: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await api({
        method: 'get',
        url: `v2/therapist/${therapistId}/client/${clientId}`,
      });
      dispatch(slice.actions.getCurrentClient(response.data));
    } catch (error) {
      console.error('Failed to fetch client details:', error);
    }
  };

export default slice;
